import { createMapScene, type MapScene } from '@assets/core/map';
import { loadMapData, loadTotalDebt } from './api';
import { detectMapCapabilities } from './capability';
import {
  getMapPageDom,
  setCanvasPlaceholderHidden,
  setMapPageStage,
  setTotalDebtText,
} from './dom';
import { hideMapError, renderMapError, renderTotalDebtError } from './error-state';
import { initializeDialog } from './initialize-dialog';
import { initializeTooltip } from './initialize-tooltip';
import { registerMapInteractions, synchronizeMapSceneAccessibility } from './interactions-map';
import { toMapRenderModel } from './map-render-model-adapter';
import { renderLegend } from './render-legend';
import { createMapState } from './state';

/**
 * Initializes the new `/map` page architecture and wires the feature modules together.
 */
document.addEventListener('DOMContentLoaded', () => {
  const mapDom = getMapPageDom();
  if (mapDom === null) {
    return;
  }

  const capabilities = detectMapCapabilities();
  const state = createMapState({
    hasHoverCapability: capabilities.hasHoverCapability,
    hasFinePointer: capabilities.hasFinePointer,
  });

  const tooltipApi = initializeTooltip({
    mapDom,
    state,
  });
  const dialogApi = initializeDialog({
    mapDom,
  });
  let mapScene: MapScene | null = null;
  let isMapPageActive = true;

  /**
   * Releases the renderer-owned SVG before this document leaves the active page lifecycle.
   *
   * @returns Nothing. Clears the local facade so bfcache restoration creates a fresh scene.
   */
  const destroyMapScene = (): void => {
    mapScene?.destroy();
    mapScene = null;
  };

  /**
   * Loads the map DTO and refreshes page-owned legend and shared SVG scene.
   *
   * The function owns `/map` loading/error UX and ignores a completed request after `pagehide`.
   * The shared renderer owns only SVG nodes and is never given tooltip, dialog or API data.
   *
   * @returns Promise resolved after the request rendered, entered the visible error state, or was
   * ignored because the page is inactive.
   */
  const reloadMap = async (): Promise<void> => {
    hideMapError({
      mapDom,
    });

    try {
      const mapData = await loadMapData();
      const renderModel = toMapRenderModel(mapData);
      if (!isMapPageActive) {
        return;
      }

      state.mapData = mapData;
      renderLegend({
        mapDom,
        mapData,
        state,
      });
      if (mapScene === null) {
        mapScene = createMapScene({
          mountElement: mapDom.canvas,
          model: renderModel,
          visibleLayers: ['fields', 'field-labels', 'streets', 'cameras', 'camera-labels'],
          accessibility: {
            role: 'img',
            label: 'SVG-карта участков СНТ Фрунзенец',
          },
          rootClassNames: ['block'],
        });
        mapScene.render();
      } else {
        mapScene.replaceData(renderModel);
      }
      synchronizeMapSceneAccessibility({
        mapDom,
        state,
      });
      setCanvasPlaceholderHidden({
        mapDom,
        hidden: true,
      });
      setMapPageStage({
        mapDom,
        stage: 'architecture-ready',
      });
    } catch (error) {
      if (!isMapPageActive) {
        return;
      }

      console.error('Не удалось загрузить данные карты из /api/v1/map.', error);
      renderMapError({
        mapDom,
        message: 'Не удалось загрузить карту.',
      });
      setMapPageStage({
        mapDom,
        stage: 'map-error',
      });
    }
  };

  /**
   * Loads the overall total-debt label for the page-level block below the map.
   *
   * @returns Promise resolved after the formatted backend value or a non-blocking fallback is shown;
   * completed requests are ignored after `pagehide`.
   */
  const reloadTotalDebt = async (): Promise<void> => {
    try {
      const totalDebtResult = await loadTotalDebt();
      if (!isMapPageActive) {
        return;
      }

      setTotalDebtText({
        mapDom,
        text: totalDebtResult.totalDebtText,
      });
    } catch (error) {
      if (!isMapPageActive) {
        return;
      }

      console.error(
        'Не удалось загрузить общую сумму задолженности из /api/v1/debtors/total_debt.',
        error,
      );
      renderTotalDebtError({
        mapDom,
      });
    }
  };

  registerMapInteractions({
    mapDom,
    state,
    tooltipApi,
    dialogApi,
    reloadMap,
    getEntityFromTarget: (target) => mapScene?.getEntityFromTarget(target) ?? null,
  });

  /**
   * Marks the page inactive and releases SVG DOM before navigation or bfcache suspension.
   *
   * @returns Nothing.
   */
  const handlePageHide = (): void => {
    isMapPageActive = false;
    destroyMapScene();
  };

  /**
   * Reloads map data and debt summary after a persisted bfcache restoration.
   *
   * @param event Browser page-show event; only persisted restores need scene recreation.
   * @returns Nothing. Both page reloads start asynchronously once lifecycle state is active.
   */
  const handlePageShow = (event: PageTransitionEvent): void => {
    if (!event.persisted) {
      return;
    }

    isMapPageActive = true;
    void reloadMap();
    void reloadTotalDebt();
  };

  window.addEventListener('pagehide', handlePageHide);
  window.addEventListener('pageshow', handlePageShow);

  setMapPageStage({
    mapDom,
    stage: 'booting',
  });

  void reloadMap();
  void reloadTotalDebt();
});
